import type { ItemDetails } from '@/components/views/published/types';
import type { SystemFieldsSchema, SystemFieldsTabAdapter } from '../types';

interface CreatePublishedSystemFieldsAdapterOptions {
  itemDetails: ItemDetails;
}

export const createPublishedSystemFieldsAdapter = ({
  itemDetails,
}: CreatePublishedSystemFieldsAdapterOptions): SystemFieldsTabAdapter => {
  const getFieldValue = <K extends keyof SystemFieldsSchema>(field: K): SystemFieldsSchema[K] => {
    switch (field) {
      case 'createdBy':
        return itemDetails.createdBy as SystemFieldsSchema[K];
      case 'createdAt':
        return itemDetails.createdAt as SystemFieldsSchema[K];
      case 'updatedBy':
        return itemDetails.updatedBy as SystemFieldsSchema[K];
      case 'updatedAt':
        return itemDetails.updatedAt as SystemFieldsSchema[K];
      case 'doneDateTime':
        return itemDetails.doneDateTime as SystemFieldsSchema[K];
      case 'externalKey':
        return itemDetails.externalKey as SystemFieldsSchema[K];
      case 'externalLink':
        return itemDetails.externalLink as SystemFieldsSchema[K];
      case 'planId':
        return itemDetails.planId as SystemFieldsSchema[K];
      case 'expectedStartDateTime':
        return undefined as SystemFieldsSchema[K];
      case 'expectedFinishDateTime':
        return undefined as SystemFieldsSchema[K];
      case 'latestFinishDateTime':
        return undefined as SystemFieldsSchema[K];
      default: {
        const _exhaustiveCheck: never = field;
        return _exhaustiveCheck;
      }
    }
  };

  const isEditable = (_field: keyof SystemFieldsSchema): boolean => {
    return false;
  };

  const isDisabled = (_field: keyof SystemFieldsSchema): boolean => {
    return false;
  };

  const isVisible = (field: keyof SystemFieldsSchema): boolean => {
    // Hide calculated schedule fields for published views
    if (
      field === 'expectedStartDateTime' ||
      field === 'expectedFinishDateTime' ||
      field === 'latestFinishDateTime'
    ) {
      return false;
    }
    return true;
  };

  const getFieldSchema = (): SystemFieldsSchema => {
    return {
      createdBy: itemDetails.createdBy,
      createdAt: itemDetails.createdAt,
      updatedBy: itemDetails.updatedBy,
      updatedAt: itemDetails.updatedAt,
      doneDateTime: itemDetails.doneDateTime ?? undefined,
      externalKey: itemDetails.externalKey ?? undefined,
      externalLink: itemDetails.externalLink ?? undefined,
      planId: itemDetails.planId ?? undefined,
      expectedStartDateTime: undefined,
      expectedFinishDateTime: undefined,
      latestFinishDateTime: undefined,
    };
  };

  return {
    getFieldValue,
    isEditable,
    isDisabled,
    isVisible,
    getFieldSchema,
    showCalculatedSchedule: false, // No calculated schedule in published views
  };
};
